import { memo, useCallback, useMemo } from 'react';
import { Box, Stack } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { HighwayArea, HighwayPoiSpec } from '@thive/fddm-types';
import { MathUtils } from 'three';

import MarginHorizontalIcon from '@/assets/icons/MarginHorizontalIcon';
import MarginVerticalIcon from '@/assets/icons/MarginVerticalIcon';
import { GapHorizontalIcon, GapVerticalIcon } from '@/assets/icons';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumUtils } from '@/modules/common/utils';
import { SelectInput } from '@/modules/common/components/inputs/components/SelectInput';
import { NumberInput } from '@/modules/common/components/inputs/components/NumberInput';
import { RectangleUtils } from '@/modules/floorplanDesign/helpers/RectangleUtils';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getCommonNumber, getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { useTranslatedOptions } from '@/modules/common/hooks';

const stackSx = { width: '100%' };

const DISTRIBUTION_OPTIONS: {
  labelKey: string;
  value: HighwayPoiSpec['horizontalDistribution'] | HighwayPoiSpec['verticalDistribution'];
}[] = [
  { labelKey: 'interface:properties.area.distribution_options.margin', value: 'MARGIN' },
  { labelKey: 'interface:properties.area.distribution_options.gap', value: 'GAP' },
];

type Props = {
  selectedHighways: HighwayArea[];
};

type PoiSpec = HighwayArea['highwayPoiSpecs'][number];

/** @description Icons follow a straight highway's direction; angled highways keep them unrotated. */
const getHighwayIconRotationDeg = ({ segments }: HighwayArea): number => {
  if (segments.length > 1) return 0;

  return MathUtils.radToDeg(RectangleUtils.fromSegment(segments[0]).angle) * -1;
};

const withUpdatedPoiSpecs = (
  highway: HighwayArea,
  updateSpec: (spec: PoiSpec) => PoiSpec,
): HighwayArea => ({
  ...highway,
  highwayPoiSpecs: minItemsArrayUtils.mapAtLeast1(highway.highwayPoiSpecs, updateSpec),
});

function HighwaySpacingComponent({ selectedHighways }: Props) {
  const distributionOptions = useTranslatedOptions(DISTRIBUTION_OPTIONS);
  const { t } = useTranslation(['interface']);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const highwayIconRotationDeg = useMemo(
    () => getCommonNumber(selectedHighways.map(getHighwayIconRotationDeg)) ?? 0,
    [selectedHighways],
  );

  // null means the selected highways have different values ('Mixed')
  const commonValues = useMemo(() => {
    const specs = selectedHighways.map(({ highwayPoiSpecs }) => highwayPoiSpecs[0]);
    const getCommonMm = (values: number[]) =>
      getCommonNumber(values.map((value) => NumUtils.metersToMmRounded(value)));

    return {
      horizontalDistribution: getCommonOption(specs.map((spec) => spec.horizontalDistribution)),
      horizontalMinMargin: getCommonMm(specs.map((spec) => spec.horizontalSpacing.minMargin)),
      horizontalMinGap: getCommonMm(specs.map((spec) => spec.horizontalSpacing.minGap)),
      verticalDistribution: getCommonOption(specs.map((spec) => spec.verticalDistribution)),
      verticalMinMargin: getCommonMm(specs.map((spec) => spec.verticalSpacing.minMargin)),
      verticalMinGap: getCommonMm(specs.map((spec) => spec.verticalSpacing.minGap)),
    };
  }, [selectedHighways]);

  const iconSx = useMemo(
    () => ({
      display: 'inline-flex',
      lineHeight: 0,
      transform: `rotate(${highwayIconRotationDeg}deg)`,
      transformOrigin: 'center',
    }),
    [highwayIconRotationDeg],
  );

  const horizontalMarginIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <MarginHorizontalIcon />
      </Box>
    ),
    [iconSx],
  );

  const horizontalGapIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <GapHorizontalIcon />
      </Box>
    ),
    [iconSx],
  );

  const verticalMarginIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <MarginVerticalIcon />
      </Box>
    ),
    [iconSx],
  );

  const verticalGapIcon = useMemo(
    () => (
      <Box component='span' sx={iconSx}>
        <GapVerticalIcon />
      </Box>
    ),
    [iconSx],
  );

  /** Applies the same spec change to every selected highway in one update (one undo step). */
  const updateSpecs = useCallback(
    (updateSpec: (spec: PoiSpec) => PoiSpec) => {
      const updatedAreas = createEmptyAreas();
      selectedHighways.forEach((highway) => {
        pushAreaToItsCollection(updatedAreas, withUpdatedPoiSpecs(highway, updateSpec));
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, selectedHighways],
  );

  const onHorizontalDistributionChange = useCallback(
    (newValue: HighwayPoiSpec['horizontalDistribution']) => {
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalDistribution', newValue);
      updateSpecs((spec) => ({ ...spec, horizontalDistribution: newValue }));
    },
    [fddmSvc, updateSpecs],
  );

  const onHorizontalMinMarginChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minMargin', newValue);
      updateSpecs((spec) => ({
        ...spec,
        horizontalSpacing: { ...spec.horizontalSpacing, minMargin: newValue },
      }));
    },
    [fddmSvc, updateSpecs],
  );

  const onHorizontalMinGapChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.horizontalSpacing.minGap', newValue);
      updateSpecs((spec) => ({
        ...spec,
        horizontalSpacing: { ...spec.horizontalSpacing, minGap: newValue },
      }));
    },
    [fddmSvc, updateSpecs],
  );

  const onVerticalDistributionChange = useCallback(
    (newValue: HighwayPoiSpec['verticalDistribution']) => {
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalDistribution', newValue);
      updateSpecs((spec) => ({ ...spec, verticalDistribution: newValue }));
    },
    [fddmSvc, updateSpecs],
  );

  const onVerticalMinMarginChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minMargin', newValue);
      updateSpecs((spec) => ({
        ...spec,
        verticalSpacing: { ...spec.verticalSpacing, minMargin: newValue },
      }));
    },
    [fddmSvc, updateSpecs],
  );

  const onVerticalMinGapChange = useCallback(
    (newValueMm: number) => {
      const newValue = NumUtils.mmToMeters(newValueMm);
      fddmSvc.setPreference('highwayArea.highwayPoiSpecs.verticalSpacing.minGap', newValue);
      updateSpecs((spec) => ({
        ...spec,
        verticalSpacing: { ...spec.verticalSpacing, minGap: newValue },
      }));
    },
    [fddmSvc, updateSpecs],
  );

  const {
    horizontalDistribution,
    horizontalMinMargin,
    horizontalMinGap,
    verticalDistribution,
    verticalMinMargin,
    verticalMinGap,
  } = commonValues;

  return (
    <>
      <PropertiesLabel i18nkey='interface:properties.area.horizontal_distribution'>
        <SelectInput
          options={distributionOptions}
          onChange={onHorizontalDistributionChange}
          value={horizontalDistribution}
          showMixed
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.horizontal_spacing'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={1}
          sx={stackSx}
        >
          <NumberInput
            value={horizontalMinMargin}
            unit='mm'
            prefix={horizontalMarginIcon}
            onChange={onHorizontalMinMarginChange}
            tooltip={t('properties.highway.tooltips.routing_point_margin_to_crossing')}
          />
          <NumberInput
            value={horizontalMinGap}
            unit='mm'
            prefix={horizontalGapIcon}
            onChange={onHorizontalMinGapChange}
            tooltip={t('properties.highway.tooltips.routing_point_group_min_gap')}
          />
        </Stack>
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.vertical_distribution'>
        <SelectInput
          options={distributionOptions}
          onChange={onVerticalDistributionChange}
          value={verticalDistribution}
          showMixed
        />
      </PropertiesLabel>

      <PropertiesLabel i18nkey='interface:properties.area.vertical_spacing'>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          spacing={1}
          sx={stackSx}
        >
          <NumberInput
            value={verticalMinMargin}
            unit='mm'
            prefix={verticalMarginIcon}
            onChange={onVerticalMinMarginChange}
            tooltip={t('properties.highway.tooltips.margin')}
          />
          <NumberInput
            value={verticalMinGap}
            unit='mm'
            prefix={verticalGapIcon}
            onChange={onVerticalMinGapChange}
            tooltip={t('properties.highway.tooltips.gap')}
          />
        </Stack>
      </PropertiesLabel>
    </>
  );
}

export const HighwaySpacing = memo(HighwaySpacingComponent);
